import { ReactElement, useEffect, useState } from 'react';
import { Box, FormControlLabel, IconButton, Switch, Typography } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { LoadingFragment, SubscriptionsList } from '@localstack/ui';
import { AdminService, useApiGetter, useRoutes } from '@localstack/services';
import { SUB_ACTIVE_STATUSES } from '@localstack/constants';

import { AdminPermission, FeatureTuple } from '@localstack/types';

import { AppRoute } from '~/config';
import { useAuthProvider } from '~/hooks/useAuthProvider';

export interface SubscriptionsProps {
  orgId: string;
}

type FeatureSet = {
  default: FeatureTuple[];
  add: FeatureTuple[];
  remove: FeatureTuple[];
};

export const Subscriptions = ({ orgId }: SubscriptionsProps): ReactElement => {
  const { can } = useAuthProvider();
  const { goto } = useRoutes();

  const [showCancelledSubs, setShowCancelledSubs] = useState(false);

  const [subscriptionFeatures, setSubscriptionFeatures] = useState<Record<string, FeatureSet>>({});

  const { data: organization } = useApiGetter(AdminService, 'getOrganization', [orgId]);

  const { data: subscriptions, isLoading: isSubsLoading } = useApiGetter(
    AdminService,
    'listOrganizationSubscriptions',
    [orgId],
  );

  const { data: licenseAssignments, isLoading: isLicensesLoading } = useApiGetter(
    AdminService,
    'listOrganizationLicenseAssignments',
    [organization?.id || ''],
    { enable: !!organization },
  );

  const activeSubscriptions = (subscriptions ?? []).filter(({ status }) => SUB_ACTIVE_STATUSES.includes(status));

  useEffect(() => {
    const fetchAllFeatures = async () => {
      if (!orgId || !subscriptions?.length) return;

      const result: Record<string, FeatureSet> = {};

      await Promise.all(
        subscriptions.map(async (sub) => {
          try {
            const features = await AdminService.listSubscriptionFeatures(orgId, sub.id);

            result[sub.id] = {
              default: features.default as FeatureTuple[],
              add: features.add as FeatureTuple[],
              remove: features.remove as FeatureTuple[],
            };
          } catch (err) {
            console.error(`Failed to fetch features for ${sub.id}`, err);
          }
        }),
      );

      setSubscriptionFeatures(result);
    };

    fetchAllFeatures();
  }, [orgId, subscriptions]);

  return (
    <>
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Typography variant="caption" align="right" component="div">
          Subscriptions
        </Typography>
        <Box display="flex" alignItems="center">
          <IconButton
            color="primary"
            onClick={() => goto(AppRoute.ADMIN_ORGANIZATION_SUBSCRIBE, { orgId })}
            disabled={!can(AdminPermission.WORKSPACES_WRITE)}
          >
            <AddIcon />
          </IconButton>
          <FormControlLabel
            control={
              <Switch
                color="primary"
                checked={showCancelledSubs}
                onChange={(_, checked: boolean) => setShowCancelledSubs(checked)}
              />
            }
            label="Show Cancelled"
          />
        </Box>
      </Box>
      <LoadingFragment
        loading={isSubsLoading || isLicensesLoading}
        arrayData={subscriptions ?? []}
        height={40}
        size={5}
      >
        <SubscriptionsList
          showCancelled
          isAdminPanel
          licenseAssignments={licenseAssignments ?? []}
          subscriptions={showCancelledSubs ? (subscriptions ?? []) : activeSubscriptions}
          onViewSubscription={(subscription) =>
            goto(AppRoute.ADMIN_ORGANIZATION_SUBSCRIPTION, { orgId, subscriptionId: subscription.id })
          }
          subscriptionFeatures={subscriptionFeatures}
        />
      </LoadingFragment>
    </>
  );
};
